import { getElementsByAttribute, getElementsByType } from '../../Utils/SlateUtils';
import { getBaseHeightInPts } from '../pageLayoutMath';

/**
 * Build the superscriptsObject lookup from a raw superscript list and wrapper list.
 * Extracted so it can be reused when the DOM has changed mid-run.
 *
 * @param {Array}    supList      - raw [node, path][] from getElementsByAttribute
 * @param {Array}    wrappersList - raw [node, path][] from getElementsByType footnote-wrapper
 * @param {function} getHeight    - (slateNode) => number; resolves DOM height for a content node
 */
export function buildSuperscriptsObject(supList, wrappersList, getHeight) {
	return supList.reduce((acc, curr) => {
		const foundWrapper = wrappersList.find(
			(fnw) => fnw[0].children.some((child) => child.superscriptNumber === curr[0].text)
		);
		const footnoteContententPath = foundWrapper
			? [
				foundWrapper[1][0],
				foundWrapper[0].children.findIndex((child) => child.superscriptNumber === curr[0].text),
			]
			: null;
		const contentNode = foundWrapper ? foundWrapper[0].children[footnoteContententPath[1]] : null;
		return {
			...acc,
			[curr[0].text]: {
				at: curr[1],
				superscriptNumber: curr[0].text,
				content: contentNode,
				contentPath: footnoteContententPath,
				contentHeight: contentNode ? getHeight(contentNode) : 0,
			},
		};
	}, {});
}

/**
 * Collect all static data needed by the footnote placement algorithm.
 *
 * Covers action-plan steps 1–3:
 *   1. Page geometry — base content heights in px and pt for each margin variant.
 *   2. Change-tracking arrays — mutable accumulators returned pre-initialised so
 *      callers don't have to declare them.
 *   3. Slate model queries — identify every footnote-wrapper, footnote-content, and
 *      superscript node, then build stable lookup structures that previously were
 *      rebuilt on every forEach iteration.
 *
 * @param {{
 *   editor:            object,    - Slate editor instance
 *   margins:           object,    - { top, bottom, left, right } in pts
 *   size:              object,    - { width, height } in pts
 *   firstPageMargins:  object|null,
 *   lastPageMargins:   object|null,
 *   styles:            object,    - style definitions keyed by name
 *   flowStyles:        object,    - flowing-config style map
 *   toDOMHeight:       function,  - (editor, slateNode) => number (px)
 *                                   Injectable so tests can avoid real DOM.
 *                                   Default: ReactEditor + getBoundingClientRect.
 * }}
 */
export function collectFootnoteData({
	editor,
	margins,
	size,
	firstPageMargins,
	lastPageMargins,
	styles,
	flowStyles,
	toDOMHeight,
}) {
	// ── STEP ONE: Page geometry ────────────────────────────────────────────────
	const basePageHeightInPts = getBaseHeightInPts(margins, size);
	const baseFirstPageHeightInPts = firstPageMargins && getBaseHeightInPts(firstPageMargins, size);
	const baseLastPageHeightInPts = lastPageMargins && getBaseHeightInPts(lastPageMargins, size);
	const baseFirstAndLastPageHeightInPts = firstPageMargins && lastPageMargins
		? getBaseHeightInPts({ top: firstPageMargins.top, bottom: lastPageMargins.bottom }, size)
		: null;

	// ── STEP TWO: Change-tracking arrays ──────────────────────────────────────
	const newFootnoteWrappersCreated = [];
	const deletedFootnoteWrappers = [];
	const listsItemsToDelete = [];
	const listsItemsToAdd = [];
	const newListsNodes = [];
	const hasRelocatedFootnoteContent = {};
	const fnwToBeMoved = [];
	const fnwInTheWrongPage = [];

	// ── STEP THREE: Slate model queries ───────────────────────────────────────
	const allFootnoteWrappers = getElementsByType(editor, 'footnote-wrapper', false);
	const allFootnoteContent = getElementsByType(editor, 'footnote-content', false);
	const allSuperscripts = getElementsByAttribute(editor, 'superscript');

	// Pre-build lookup structures.
	// These only depend on the Slate model (not DOM measurements or the forEach
	// index), so computing them once here avoids redundant work that previously
	// happened on every footnote-wrapper iteration.

	/**
	 * fnWLists — one entry per footnote-wrapper, listing the superscript numbers
	 * of its children.  Shape: Array<{ at: number[], content: string[] }>
	 */
	const fnWLists = allFootnoteWrappers.map((fnw) => ({
		at: fnw[1],
		content: fnw[0].children.map((fnc) => fnc.superscriptNumber),
	}));

	/**
	 * footnoteContentObject — keyed by superscriptNumber.
	 * Shape: Record<string, { at: number[], superscriptNumber: string, content: object }>
	 */
	const footnoteContentObject = allFootnoteContent.reduce((acc, curr) => ({
		...acc,
		[curr[0].superscriptNumber]: {
			at: curr[1],
			superscriptNumber: curr[0].superscriptNumber,
			content: curr[0],
		},
	}), {});

	/**
	 * superscriptsObject — keyed by superscript text value.
	 * contentHeight is resolved via the injected toDOMHeight callback so this
	 * function can be exercised in tests without a real DOM.
	 * Shape: Record<string, { at, superscriptNumber, content, contentPath, contentHeight }>
	 */
	const superscriptsObject = buildSuperscriptsObject(
		allSuperscripts,
		allFootnoteWrappers,
		(node) => (toDOMHeight ? toDOMHeight(editor, node) : 0),
	);

	return {
		// STEP ONE
		basePageHeightInPts,
		baseFirstPageHeightInPts,
		baseLastPageHeightInPts,
		baseFirstAndLastPageHeightInPts,
		// STEP TWO (mutable — caller pushes into these)
		newFootnoteWrappersCreated,
		deletedFootnoteWrappers,
		listsItemsToDelete,
		listsItemsToAdd,
		newListsNodes,
		hasRelocatedFootnoteContent,
		fnwToBeMoved,
		fnwInTheWrongPage,
		// STEP THREE
		allFootnoteWrappers,
		allFootnoteContent,
		allSuperscripts,
		// Derived lookups
		fnWLists,
		footnoteContentObject,
		superscriptsObject,
	};
}
